import { ApiEndpointTags } from '../imago-mp.types';
import { EntityState } from './entity-state';
import type {
    EntitySearchArg,
    PageParam,
    PageResult,
    PagedMetaEnvelope,
    PagedQueryArg,
} from './paged-types';
import { SEARCH_RESULT_LIMIT } from './paged-types';
import { buildPagedQueryString, buildSearchWhere } from './query-params';

/** Columns the subscription (instance) search matches against. */
export const SUBSCRIPTION_SEARCH_COLUMNS = [
    'subscriptionName',
    'customerName',
    'companyName',
    'salesforceLink',
    'planCode',
    'region',
    'comment',
    'capacityGroupName',
] as const;

/** UUID columns included as exact-match when the search term is a full UUID. */
export const SUBSCRIPTION_UUID_COLUMNS = ['id', 'capacityGroupId'] as const;

export type Addon = {
    id: string;
    name: string;
    quantity: number;
    type: string;
    salesforceQuoteNumber: string;
    notes: string;
    startDate: string;
    finishDate: string;
    state: string;
};

export type AddonApiItem = Partial<Addon>;

const hasAddonId = (a: AddonApiItem): a is AddonApiItem & { id: string } =>
    typeof a.id === 'string';

export const transformAddon = (a: AddonApiItem & { id: string }): Addon => ({
    id: a.id,
    name: a.name ?? '',
    quantity: a.quantity ?? 0,
    type: a.type ?? '',
    salesforceQuoteNumber: a.salesforceQuoteNumber ?? '',
    notes: a.notes ?? '',
    startDate: a.startDate ?? '',
    finishDate: a.finishDate ?? '',
    state: a.state ?? EntityState.INACTIVE,
});

export type Subscription = {
    id: string;
    subscriptionName: string;
    customerName: string;
    companyName: string;
    salesforceLink: string;
    comment: string;
    state: string;
    tenantId: string;
    capacityGroupId: string;
    capacityGroupName: string;
    addons: Addon[];
};

export type SubscriptionApiItem = Partial<Omit<Subscription, 'addons'>> & {
    addons?: AddonApiItem[];
};

type SubscriptionsApiResponse = {
    subscriptions?: SubscriptionApiItem[];
    meta?: PagedMetaEnvelope;
};

type SubscriptionApiItemWithId = SubscriptionApiItem & { id: string };

const hasId = (sub: SubscriptionApiItem): sub is SubscriptionApiItemWithId =>
    typeof sub.id === 'string';

export const transformSubscription = (sub: SubscriptionApiItemWithId): Subscription => ({
    id: sub.id,
    subscriptionName: sub.subscriptionName ?? '',
    customerName: sub.customerName ?? '',
    companyName: sub.companyName ?? '',
    salesforceLink: sub.salesforceLink ?? '',
    comment: sub.comment ?? '',
    state: sub.state ?? EntityState.INACTIVE,
    tenantId: sub.tenantId ?? '',
    capacityGroupId: sub.capacityGroupId ?? '',
    capacityGroupName: sub.capacityGroupName ?? '',
    addons: Array.isArray(sub.addons) ? sub.addons.filter(hasAddonId).map(transformAddon) : [],
});

export type SubscriptionsPageResult = PageResult<Subscription>;
export type SubscriptionsQueryArg = PagedQueryArg;

export const subscriptionsInfiniteQuery = {
    query: ({
        queryArg,
        pageParam,
    }: {
        queryArg: SubscriptionsQueryArg;
        pageParam: PageParam;
    }) => ({
        url: `/mp/1/subscriptions?${buildPagedQueryString({
            offset: pageParam.offset,
            limit: queryArg.limit,
            includeCount: true,
            order: queryArg.order,
            where: queryArg.where,
        })}`,
        method: 'get',
    }),
    transformResponse: (response: SubscriptionsApiResponse): SubscriptionsPageResult => {
        const rows = (response?.subscriptions ?? []).filter(hasId).map(transformSubscription);
        return {
            rows,
            total: response?.meta?.total ?? rows.length,
            offset: response?.meta?.offset ?? 0,
            limit: response?.meta?.limit ?? rows.length,
        };
    },
    // Keep loaded pages alive across navigation so revisits render instantly
    // from cache instead of cold-fetching with a skeleton. Re-render cost of
    // large cached lists is handled by row virtualization in DataTable.
    keepUnusedDataFor: 300,
    infiniteQueryOptions: {
        initialPageParam: { offset: 0 },
        getNextPageParam: (
            lastPage: SubscriptionsPageResult,
            _allPages: SubscriptionsPageResult[],
            lastPageParam: PageParam,
        ): PageParam | undefined => {
            if (lastPage.rows.length === 0) return undefined;
            const loaded = lastPageParam.offset + lastPage.rows.length;
            return loaded < lastPage.total ? { offset: loaded } : undefined;
        },
        getPreviousPageParam: (): PageParam | undefined => undefined,
    },
    providesTags: [ApiEndpointTags.Subscriptions],
};

/**
 * Subscriptions list endpoint used only as a small lookup for joining
 * customerName onto the paginated users list. Pagination is intentionally not
 * applied here — the dataset is small. Will be removed when the backend
 * embeds subscription on user.
 */
export const subscriptionsLookupQuery = {
    query: () => ({
        url: '/mp/1/subscriptions',
        method: 'get',
    }),
    transformResponse: (response: SubscriptionsApiResponse): Subscription[] => {
        if (!response?.subscriptions) return [];
        return response.subscriptions.filter(hasId).map(transformSubscription);
    },
};

export type SubscriptionsLookupResponse = Subscription[];

/**
 * Lightweight, non-paginated search used by the global TopBar typeahead.
 * Returns at most `limit` matching subscriptions ordered by name.
 */
export const searchSubscriptionsQuery = {
    query: ({ term, limit = SEARCH_RESULT_LIMIT + 1, where }: EntitySearchArg) => ({
        url: `/mp/1/subscriptions?${buildPagedQueryString({
            offset: 0,
            limit,
            order: [['subscriptionName', 'ASC']],
            where: {
                ...buildSearchWhere(SUBSCRIPTION_SEARCH_COLUMNS, term, SUBSCRIPTION_UUID_COLUMNS),
                ...where,
            },
        })}`,
        method: 'get',
    }),
    transformResponse: (response: SubscriptionsApiResponse): Subscription[] =>
        (response?.subscriptions ?? []).filter(hasId).map(transformSubscription),
};
